/**
 * Organizations Hooks (Phase 30 Wave 4 chunks 30.4.3b + 30.4.3c)
 *
 * Super-admin-only cross-org administration. Consumes the routes added in
 * chunk 30.4.3a at `/api/admin/orgs/*` (see `apps/api/src/routes/admin/orgs.ts`).
 *
 * Mirrors the patterns at `hooks/useUsers.ts`:
 * - Query-key factory at the top of the file.
 * - `useApi()` wrapper for authenticated fetch.
 * - Mutations invalidate related caches in `onSuccess`.
 *
 * Chunk 30.4.3b shipped the master-list + create flow:
 *   - `useOrganizations()`
 *   - `useCreateOrganization()`
 *
 * Chunk 30.4.3c extends with detail-panel + Add User + per-org settings:
 *   - `useOrganization(orgId)`
 *   - `useOrgUsers(orgId)`
 *   - `useOrgSectionsLookup(orgId)`
 *   - `useAddOrgUser()`
 *   - `useUpdateOrgSettings()`
 *
 * Phase 36.3 chunk 3 adds per-user management to the detail panel:
 *   - `useUpdateOrgUser(orgId)`
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth0 } from '@auth0/auth0-react';
import { useApi } from '../lib/hooks/useApi';
import { ApiError } from '../lib/api';

const API_BASE_URL = import.meta.env.VITE_API_URL || '';

// ============================================
// TYPES — match the GET /api/admin/orgs payload from chunk 30.4.3a.
// Defined locally because there is no shared schema yet for these
// admin-only routes (the responses are server-side composed; see
// apps/api/src/routes/admin/orgs.ts:157-177 + :972-990).
// ============================================

export interface OrganizationListItem {
  id: string;
  name: string;
  slug: string;
  auth0OrgId: string | null;
  createdAt: Date;
  updatedAt: Date;
  userCount: number;
  trainingDistributionEnabled: boolean;
  livingSopsEnabled: boolean;
  // Phase 36.1d — resolved `=== true` server-side (OPT-IN), unlike the two
  // above which resolve `!== false`.
  supervisedModeEnabled: boolean;
}

export interface CreateOrganizationInput {
  name: string;
  slug: string;
  auth0OrgId?: string | null;
}

export interface CreatedOrganization {
  id: string;
  name: string;
  slug: string;
  auth0OrgId: string | null;
  settings: Record<string, unknown>;
  createdAt: Date;
  updatedAt: Date;
}

// ============================================
// CHUNK 30.4.3c TYPES
// ============================================

export interface OrgUser {
  id: string;
  email: string;
  fullName: string | null;
  role: 'super_admin' | 'admin' | 'trainer' | 'manager' | 'operator';
  isActive: boolean;
  lastLoginAt: Date | string | null;
  createdAt: Date | string;
}

export interface OrganizationSettings {
  trainingDistribution?: {
    enabled?: boolean;
    scopeSectionIds?: string[] | null;
  };
  livingSops?: {
    // Bug D-2: only `enabled` is read at runtime; UI must NOT expose
    // `scopeSectionIds` or `scopeSources` here.
    enabled?: boolean;
  };
  // Phase 36.1d. Both keys have runtime readers in
  // `services/supervised-mode.service.ts`, so neither repeats the D-2 pattern.
  supervisedMode?: {
    enabled?: boolean;
    scopeScheduleIds?: string[] | null;
  };
}

export interface OrganizationDetail {
  id: string;
  name: string;
  slug: string;
  auth0OrgId: string | null;
  settings: OrganizationSettings;
  createdAt: Date | string;
  updatedAt: Date | string;
  userCount: number;
  users: OrgUser[];
  trainingDistributionEnabled: boolean;
  livingSopsEnabled: boolean;
  // Phase 36.1d — resolved `=== true` server-side (OPT-IN), unlike the two
  // above which resolve `!== false`.
  supervisedModeEnabled: boolean;
}

export interface OrgSectionLookupItem {
  id: string;
  title: string;
  sopId: string;
  sopTitle: string;
}

// `super_admin` deliberately excluded — backend `createOrgUserSchema`
// enum (`assignableRoleEnum`) at apps/api/src/routes/admin/orgs.ts:96 rejects it.
export type AssignableRole = 'admin' | 'trainer' | 'manager' | 'operator';

export interface AddOrgUserInput {
  email: string;
  fullName: string;
  role: AssignableRole;
}

// At least one of `role` / `isActive` must be present — the backend
// `patchOrgUserSchema` .refine() rejects an empty body
// (apps/api/src/routes/admin/orgs.ts:106-113). `fullName` is deliberately not
// patchable here; it is editable at PUT /api/users/:id and PUT /api/me.
export interface UpdateOrgUserInput {
  userId: string;
  role?: AssignableRole;
  isActive?: boolean;
}

export interface AddOrgUserResult {
  user: {
    id: string;
    email: string;
    fullName: string | null;
    role: 'super_admin' | 'admin' | 'trainer' | 'manager' | 'operator';
    isActive: boolean;
  };
  meta: {
    passwordResetSent: boolean;
    auth0UserExisted: boolean;
  };
}

export interface UpdateOrgSettingsInput {
  orgId: string;
  // JSONB shallow-merge: each cluster sent in full replaces prior cluster.
  // Send ONLY clusters being updated.
  trainingDistribution?: {
    enabled?: boolean;
    scopeSectionIds?: string[] | null;
  };
  livingSops?: {
    enabled?: boolean;
  };
  supervisedMode?: {
    enabled?: boolean;
    scopeScheduleIds?: string[] | null;
  };
}

export interface UpdatedOrgSettingsResponse {
  settings: OrganizationSettings;
  updatedAt: Date | string;
}

// ============================================
// QUERY KEYS
// ============================================

export const organizationKeys = {
  all: ['organizations'] as const,
  lists: () => [...organizationKeys.all, 'list'] as const,
  list: (filters?: Record<string, unknown>) =>
    [...organizationKeys.lists(), filters ?? {}] as const,
  details: () => [...organizationKeys.all, 'detail'] as const,
  detail: (orgId: string) => [...organizationKeys.details(), orgId] as const,
  users: (orgId: string) =>
    [...organizationKeys.details(), orgId, 'users'] as const,
  sectionsLookup: (orgId: string) =>
    [...organizationKeys.details(), orgId, 'sections-lookup'] as const,
};

// ============================================
// QUERIES
// ============================================

/**
 * Fetch all organizations (super_admin only).
 *
 * Returns resolved toggle states so the master list can render colored
 * chips without a second fetch. See `apps/api/src/routes/admin/orgs.ts:157-177`.
 */
export function useOrganizations() {
  const api = useApi();

  return useQuery({
    queryKey: organizationKeys.list(),
    queryFn: () => api.get<OrganizationListItem[]>('/api/admin/orgs'),
    staleTime: 60 * 1000, // 1 minute — admin-only data, no live updates expected
  });
}

// ============================================
// MUTATIONS
// ============================================

/**
 * Create a new organization (super_admin only).
 *
 * On success: invalidates the organizations list so the new row appears.
 * On 409 conflict (slug already exists): the underlying `ApiError`
 * propagates to the caller's `onError` with `status === 409` so the
 * modal can surface a field-level error on the slug input.
 */
export function useCreateOrganization() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateOrganizationInput) =>
      api.post<CreatedOrganization>('/api/admin/orgs', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: organizationKeys.lists() });
    },
  });
}

// ============================================
// CHUNK 30.4.3c HOOKS — DETAIL PANEL + ADD USER + SETTINGS
// ============================================

/**
 * Fetch full detail for one organization — metadata, user list, resolved
 * toggle states, and raw settings JSON.
 *
 * Backend returns full `users` array embedded in the detail response
 * (`apps/api/src/routes/admin/orgs.ts:972-990`). `useOrgUsers` is a
 * thin wrapper around the dedicated `/users` endpoint that the
 * detail-panel uses for refetch-after-add semantics.
 */
export function useOrganization(orgId: string | null) {
  const api = useApi();

  return useQuery({
    queryKey: orgId ? organizationKeys.detail(orgId) : ['organizations', 'detail', 'null'],
    queryFn: () => api.get<OrganizationDetail>(`/api/admin/orgs/${orgId}`),
    enabled: !!orgId,
    staleTime: 30 * 1000,
  });
}

/**
 * List users belonging to a specific org (super_admin only).
 *
 * Endpoint: `GET /api/admin/orgs/:orgId/users`. The list is also returned
 * by `useOrganization`, but this hook gives the detail-panel a dedicated
 * cache slot so an "Add user" mutation can invalidate users without
 * forcing a re-fetch of the entire org detail (which would refetch the
 * full toggle state and risk visual flicker on the cluster forms).
 */
export function useOrgUsers(orgId: string | null) {
  const api = useApi();

  return useQuery({
    queryKey: orgId ? organizationKeys.users(orgId) : ['organizations', 'detail', 'null', 'users'],
    queryFn: () => api.get<OrgUser[]>(`/api/admin/orgs/${orgId}/users`),
    enabled: !!orgId,
    staleTime: 30 * 1000,
  });
}

/**
 * Section lookup for the Training Distribution scope chip-picker.
 *
 * Endpoint: `GET /api/admin/orgs/:orgId/sections/lookup`. Returns active
 * sections for the target org. Empty array on orgs with zero SOPs is a
 * supported state — the chip-picker degrades to a "Nothing to pick yet."
 * dropdown message.
 */
export function useOrgSectionsLookup(orgId: string | null) {
  const api = useApi();

  return useQuery({
    queryKey: orgId
      ? organizationKeys.sectionsLookup(orgId)
      : ['organizations', 'detail', 'null', 'sections-lookup'],
    queryFn: () =>
      api.get<OrgSectionLookupItem[]>(`/api/admin/orgs/${orgId}/sections/lookup`),
    enabled: !!orgId,
    staleTime: 60 * 1000,
  });
}

/**
 * Patch per-org JSONB settings — Training Distribution / Living SOPs.
 *
 * Backend (`PATCH /api/admin/orgs/:orgId/settings`) does a shallow merge
 * per top-level cluster. Send only the cluster being updated; the prior
 * cluster's leaves are dropped if the cluster key is included. See
 * `apps/api/src/routes/admin/orgs.ts:900-912` for the merge semantics.
 *
 * On success: invalidates the org detail (resolved toggle states updated)
 * AND the org list (resolved chips on master-list update).
 */
export function useUpdateOrgSettings() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ orgId, ...body }: UpdateOrgSettingsInput) =>
      api.patch<UpdatedOrgSettingsResponse>(
        `/api/admin/orgs/${orgId}/settings`,
        body
      ),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: organizationKeys.detail(variables.orgId),
      });
      // Master list shows resolved toggle chips — invalidate so they
      // re-render with the new values.
      queryClient.invalidateQueries({ queryKey: organizationKeys.lists() });
    },
  });
}

/**
 * Add a user to a specific org via the Auth0 + invitations flow.
 *
 * Custom raw fetch: the backend response shape is
 *   `{ success, data: <user>, meta: <flags> }`
 * with `meta` at the OUTER envelope, not nested under `data`. Our standard
 * `useApi()` wrapper unwraps `data.data` and discards the `meta` sibling
 * (see `apps/web/src/lib/api.ts:60-69`). To preserve the
 * `auth0UserExisted` / `passwordResetSent` flags for the modal UI, this
 * hook bypasses the wrapper and reads the full envelope directly.
 *
 * On success invalidates org detail + users sub-list (the user count and
 * the user table both update).
 */
export function useAddOrgUser(orgId: string | null) {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();

  return useMutation<AddOrgUserResult, Error, AddOrgUserInput>({
    mutationFn: async (input) => {
      if (!orgId) throw new Error('orgId is required');

      const token = await getAccessTokenSilently();
      const response = await fetch(
        `${API_BASE_URL}/api/admin/orgs/${orgId}/users`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(input),
        }
      );

      const json = (await response.json()) as {
        success: boolean;
        data?: AddOrgUserResult['user'];
        meta?: AddOrgUserResult['meta'];
        error?: { code?: string; message?: string };
      };

      if (!response.ok || !json.success || !json.data) {
        throw new ApiError(
          json.error?.code ?? 'UNKNOWN_ERROR',
          json.error?.message ?? 'Failed to add user',
          response.status
        );
      }

      return {
        user: json.data,
        meta: json.meta ?? {
          passwordResetSent: false,
          auth0UserExisted: false,
        },
      };
    },
    onSuccess: () => {
      if (!orgId) return;
      queryClient.invalidateQueries({
        queryKey: organizationKeys.detail(orgId),
      });
      queryClient.invalidateQueries({
        queryKey: organizationKeys.users(orgId),
      });
      // User count is shown on the master list — invalidate too.
      queryClient.invalidateQueries({ queryKey: organizationKeys.lists() });
    },
  });
}

// ============================================
// PHASE 36.3 CHUNK 3 HOOK — PER-USER MANAGEMENT
// ============================================

/**
 * Change one user's role or activation state inside a specific org
 * (super_admin only).
 *
 * Endpoint: `PATCH /api/admin/orgs/:orgId/users/:userId` (chunk 36.3.2,
 * `apps/api/src/routes/admin/orgs.ts`). Standard `{ success, data }` envelope
 * with no `meta` sibling, so this goes through `useApi()` — the raw-fetch
 * workaround `useAddOrgUser` needs does not apply. `data` is the updated row
 * in the same shape as `GET /:orgId/users`.
 *
 * ASYMMETRY (deliberate — blueprint §2): a role change syncs to Auth0, a
 * deactivation does NOT. `blocked` is never written from this surface;
 * enforcement is the per-request DB check in orgMiddleware. A deactivated user
 * can therefore still complete an Auth0 login and is refused 403 on their
 * first API call rather than at the login screen. UI copy driving this hook
 * must not promise that they "can no longer sign in".
 *
 * Server-side rejections the caller should surface verbatim rather than
 * flatten into one generic message: 400 super_admin-target (Guard A), 400
 * last-admin (Guard B), 403 role-inversion, 404 unknown org / user.
 */
export function useUpdateOrgUser(orgId: string) {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation<OrgUser, Error, UpdateOrgUserInput>({
    mutationFn: ({ userId, ...body }) =>
      api.patch<OrgUser>(`/api/admin/orgs/${orgId}/users/${userId}`, body),
    onSuccess: () => {
      // Both slots hold the same rows and the panel falls back from one to the
      // other (`users ?? org.users`), so a stale detail would resurface the old
      // role on its next refetch.
      queryClient.invalidateQueries({
        queryKey: organizationKeys.detail(orgId),
      });
      queryClient.invalidateQueries({
        queryKey: organizationKeys.users(orgId),
      });
      // NOT organizationKeys.lists(), unlike the mutations above: nothing on
      // the master list derives from a user's role or isActive. Its
      // `userCount` is an unfiltered count() over every user in the org
      // (admin/orgs.ts:147-150), which neither a role change nor a
      // deactivation can move.
    },
  });
}
